import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import thunk from 'redux-thunk';
import clientBookedAppointmentsReducer, { initialClientBookedAppointmentsState } from './reducers/client-booked-appointments.reducer';
import clientDashboardReducer, { initialClientDashboardState } from './reducers/client-dashboard.reducer';
import clientInfoReducer, { initialClientInfoState } from './reducers/client-info.reducer';
import clientLoginReducer, { initialClientLoginState } from './reducers/client-login.reducer';
import clientMyHealersReducer, { initialClientMyHealersState } from './reducers/client-my-healers.reducer';
import clientRebookReducer, { initialClientRebookState } from './reducers/client-rebook.reducer';
import clientSettingsReducer, { initialClientSettingsState } from './reducers/client-settings.reducer';
import contactLuminaraReducer, { initialContactLuminaraState } from './reducers/contact-luminara.reducer';
import globalReducer, { initialGlobalState } from './reducers/global.reducer';
import healerAvailabilityReducer, { initialHealerAvailabilityState } from './reducers/healer-availability.reducer';
import healerBankDetailsReducer, { initialHealerBankDetailsState } from './reducers/healer-bank-details.reducer';
import healerClientHistoryReducer, { initialHealerClientHistoryState } from './reducers/healer-client-history.reducer';
import healerDashboardReducer, { initialHealerDashboardState } from './reducers/healer-dashboard.reducer';
import healerFeedbackReducer, { buildHealerFeedbackInitialState } from './reducers/healer-feedback.reducer';
import healerInfoReducer, { initialHealerInfoState } from './reducers/healer-info.reducer';
import healerProfileCommentsReducer, { initialHealerProfileCommentsState } from './reducers/healer-profile-comments.reducer';
import healerProfileReducer, { initialHealerProfileState } from './reducers/healer-profile.reducer';
import healerSelectionReducer, { initialHealerSelectionsState } from './reducers/healer-selection.reducer';
import healerSessionsReducer, { initialHealerSessionsState } from './reducers/healer-sessions.reducer';
import healerSettingsReducer, { initialHealerSettingsState } from './reducers/healer-settings.reducer';
import healloReducer, { initialHealloState } from './reducers/heallo.reducer';
import loaderReducer, { initialLoaderState } from './reducers/loading-spinner-reducer';
import meetingReducer, { initialMeetingState } from './reducers/meeting.reducer';
import paymentReducer, { initialPaymentState } from './reducers/payment.reducer';
import presessionReducer, { initialPreSessionState } from './reducers/presession.reducer';
import RescheduleBookingAsClientReducer, { initialRescheduleBookingAsClientState } from './reducers/reschedule-booking-as-client.reducer';
import ScheduleBookingAsClientReducer, { initialScheduleBookingAsClientState } from './reducers/schedule-booking-as-client.reducer';
import ScheduleBookingAsHealerReducer, { initialScheduleBookingAsHealerState } from './reducers/schedule-booking-as-healer.reducer';
import clientRequestHealerFormReducer, { initialClientRequestNewHealerState } from './reducers/client-request-healer-form.reducer';
import mySelectionsReducer, { initialMySelectionsState } from './reducers/my-selections.reducer';

const rootReducer = combineReducers({
  loadingSpinner: loaderReducer,
  heallo: healloReducer,
  meeting: meetingReducer,
  global: globalReducer,
  clientDashboard: clientDashboardReducer,
  healerDashboard: healerDashboardReducer,
  presession: presessionReducer,
  scheduleBookingAsHealer: ScheduleBookingAsHealerReducer,
  scheduleBookingAsClient: ScheduleBookingAsClientReducer,
  healerFeedback: healerFeedbackReducer,
  healerAvailability: healerAvailabilityReducer,
  payment: paymentReducer,
  contactLuminara: contactLuminaraReducer,
  rescheduleBookingAsClient: RescheduleBookingAsClientReducer,
  healerSelection: healerSelectionReducer,
  mySelections: mySelectionsReducer,
  clientSettings: clientSettingsReducer,
  clientBookedAppointments: clientBookedAppointmentsReducer,
  clientLogin: clientLoginReducer,
  healerProfile: healerProfileReducer,
  myHealers: clientMyHealersReducer,
  rebook: clientRebookReducer,
  healerSessions: healerSessionsReducer,
  healerSettings: healerSettingsReducer,
  clientInfo: clientInfoReducer,
  healerInfo: healerInfoReducer,
  healerClientHistory: healerClientHistoryReducer,
  healerProfileComments: healerProfileCommentsReducer,
  healerBankDetails: healerBankDetailsReducer,
  clientRequestNewHealer: clientRequestHealerFormReducer,
});

export type AppState = ReturnType<typeof rootReducer>;

export const buildInitStore = (): AppState => ({
  loadingSpinner: initialLoaderState(),
  heallo: initialHealloState(),
  meeting: initialMeetingState(),
  global: initialGlobalState(),
  clientDashboard: initialClientDashboardState(),
  healerDashboard: initialHealerDashboardState(),
  presession: initialPreSessionState(),
  scheduleBookingAsHealer: initialScheduleBookingAsHealerState(),
  scheduleBookingAsClient: initialScheduleBookingAsClientState(),
  healerFeedback: buildHealerFeedbackInitialState(),
  healerAvailability: initialHealerAvailabilityState(),
  payment: initialPaymentState(),
  contactLuminara: initialContactLuminaraState(),
  rescheduleBookingAsClient: initialRescheduleBookingAsClientState(),
  healerSelection: initialHealerSelectionsState(),
  mySelections: initialMySelectionsState(),
  clientSettings: initialClientSettingsState(),
  clientBookedAppointments: initialClientBookedAppointmentsState(),
  clientLogin: initialClientLoginState(),
  healerProfile: initialHealerProfileState(),
  myHealers: initialClientMyHealersState(),
  rebook: initialClientRebookState(),
  healerSessions: initialHealerSessionsState(),
  healerSettings: initialHealerSettingsState(),
  clientInfo: initialClientInfoState(),
  healerInfo: initialHealerInfoState(),
  healerClientHistory: initialHealerClientHistoryState(),
  healerProfileComments: initialHealerProfileCommentsState(),
  healerBankDetails: initialHealerBankDetailsState(),
  clientRequestNewHealer: initialClientRequestNewHealerState(),
});

export function configAppStore(dependencies: any, hydrate?: AppState) {
  return configureStore({
    reducer: rootReducer,
    preloadedState: hydrate ?? buildInitStore(),
    middleware: [thunk.withExtraArgument(dependencies)],
  });
}
